import { useEffect } from "react";
import Grid from "@mui/material/Grid";
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import { Link } from 'react-router-dom';
import useMediaQuery from '@mui/material/useMediaQuery';

// Icons
import StorageIcon from '@mui/icons-material/Storage';
import CompressIcon from '@mui/icons-material/Compress';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import WarningIcon from '@mui/icons-material/Warning';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import InsightsIcon from '@mui/icons-material/Insights';
import SecurityIcon from '@mui/icons-material/Security';
import SpeedIcon from '@mui/icons-material/Speed';
import GavelIcon from '@mui/icons-material/Gavel';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';

// Images
import dashboard from 'assets/images/platform/dashboard.png';

export default function SIEM() {
    const isMobile = useMediaQuery((theme) => theme.breakpoints.down('md'));

    useEffect(() => {
        document.title = 'PREVAIL SIEM — Intelligent Log Analytics Without the Cost';
        window.scrollTo(0, 0);
    });

    return (
        <>
            {/* ─── HERO ─── */}
            <Box
                width="100%"
                sx={{
                    minHeight: isMobile ? '100vh' : '75vh',
                    background: 'linear-gradient(160deg, #000000 0%, #1a0000 50%, #242331 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Grid container maxWidth="lg" spacing={2} p={3} mt={8} justifyContent="center" alignItems="center">
                    <Grid item xs={12} p={2} textAlign="center">
                        <Chip
                            label="COMING SOON — JOIN THE EARLY ACCESS LIST"
                            sx={{
                                background: 'rgba(217,0,0,0.2)',
                                border: '1px solid rgba(217,0,0,0.5)',
                                color: '#e88',
                                fontWeight: 'bold',
                                letterSpacing: 1,
                                mb: 3,
                                px: 1,
                            }}
                        />
                        <Typography
                            sx={{ typography: { xs: 'h4', md: 'h2' }, mb: 2 }}
                            fontWeight="bold"
                            color="grey.100"
                        >
                            All the Intelligence.<br />
                            <Typography
                                component="span"
                                sx={{ typography: { xs: 'h4', md: 'h2' } }}
                                fontWeight="bold"
                                color="prevailRed.main"
                            >
                                A Fraction of the Storage Cost.
                            </Typography>
                        </Typography>
                        <Box sx={{ maxWidth: isMobile ? '90%' : '65%', mx: 'auto' }}>
                            <Typography
                                variant="h6"
                                color="grey.400"
                                sx={{ mb: 4, mt: 2, lineHeight: 1.8 }}
                            >
                                Traditional SIEMs charge you to store every raw log line — costs that
                                balloon into tens of thousands of dollars a year. PREVAIL SIEM collects
                                everything and reduces it to metadata, slashing your storage bill without
                                sacrificing visibility, forensics, or compliance.
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
                            <Link to="/contact-us">
                                <Button
                                    variant="contained"
                                    color="prevailRed"
                                    size="large"
                                    sx={{ pl: 5, pr: 5, py: 1.5 }}
                                >
                                    <Typography sx={{ fontSize: '1.1em' }} color="grey.100">
                                        Request Early Access
                                    </Typography>
                                </Button>
                            </Link>
                            <Link to="/#contact">
                                <Button
                                    variant="outlined"
                                    size="large"
                                    sx={{ pl: 4, pr: 4, py: 1.5, borderColor: 'grey.600', color: 'grey.300' }}
                                >
                                    <Typography sx={{ fontSize: '1.1em' }}>Contact Us</Typography>
                                </Button>
                            </Link>
                        </Box>
                    </Grid>
                </Grid>
            </Box>

            {/* ─── STATS BAR ─── */}
            <Box sx={{ background: '#6b0000', py: 4 }}>
                <Grid container maxWidth="xl" spacing={2} justifyContent="center" alignItems="center"
                    textAlign="center" sx={{ mx: 'auto' }}>
                    {[
                        { stat: '90%', label: 'Storage reduction vs. raw log retention' },
                        { stat: '$0', label: 'Per-GB ingestion fees — ever' },
                        { stat: '100%', label: 'Of log events processed — nothing discarded' },
                        { stat: 'Full', label: 'Forensic and compliance capability retained' },
                    ].map(({ stat, label }) => (
                        <Grid item size={{ xs: 6, md: 3 }} key={stat}>
                            <Typography variant="h4" color="grey.100" fontWeight="bold">{stat}</Typography>
                            <Typography variant="body2" color="grey.400" mt={0.5}>{label}</Typography>
                        </Grid>
                    ))}
                </Grid>
            </Box>

            {/* ─── THE SIEM COST PROBLEM ─── */}
            <Box
                sx={{
                    background: '#242331',
                    py: 10,
                    display: 'grid',
                    placeItems: 'center',
                }}
            >
                <Grid container maxWidth="lg" spacing={2} justifyContent="center">
                    <Grid item xs={12} textAlign="center" mb={4}>
                        <Typography variant="h3" color="grey.100" fontWeight="bold">
                            The SIEM Pricing Problem Nobody Talks About
                        </Typography>
                        <Typography variant="h6" color="grey.400" mt={2} sx={{ maxWidth: '750px', mx: 'auto' }}>
                            Security Information and Event Management was supposed to give every organization
                            visibility into their environment. Instead, it became one of the most
                            expensive line items in enterprise IT — and completely out of reach for SMBs.
                        </Typography>
                    </Grid>

                    {[
                        {
                            icon: <ReceiptLongIcon sx={{ fontSize: 48 }} />,
                            title: 'Raw Log Storage Is Too Expensive',
                            description:
                                'Splunk and Microsoft Sentinel charge by the gigabyte of data ingested per day. A mid-sized company with active servers, firewalls, and cloud services can easily generate hundreds of gigabytes of logs daily. With prices as high as $100 per GB per month, the bill grows faster than the threat landscape.',
                        },
                        {
                            icon: <WarningIcon sx={{ fontSize: 48 }} />,
                            title: 'Discarding Logs Is Not the Answer',
                            description:
                                "Some platforms respond to the cost problem by simply throwing data away; discarding logs they deem unimportant to keep storage low. This saves money but creates dangerous blind spots: when an incident happens, the forensic trail is gone. You can't investigate what you didn't keep.",
                        },
                        {
                            icon: <TrendingDownIcon sx={{ fontSize: 48 }} />,
                            title: 'SMBs Are Priced Out Entirely',
                            description:
                                'The result is that meaningful SIEM capability is simply unaffordable for most small and medium businesses. They either pay too much, settle for incomplete tools, or go without — leaving them flying blind when a breach occurs.',
                        },
                    ].map(({ icon, title, description }) => (
                        <Grid item xs={12} md={4} key={title} p={1}>
                            <Box
                                sx={{
                                    background: '#1a1929',
                                    border: '1px solid rgba(217,0,0,0.35)',
                                    borderRadius: 2,
                                    p: 4,
                                    height: '100%',
                                }}
                            >
                                <Typography color="prevailRed.main" mb={2}>{icon}</Typography>
                                <Typography variant="h5" color="grey.100" fontWeight="bold" mb={2}>
                                    {title}
                                </Typography>
                                <Typography variant="body2" color="grey.400" lineHeight={1.8}>
                                    {description}
                                </Typography>
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            </Box>

            {/* ─── HOW PREVAIL SIEM IS DIFFERENT ─── */}
            <Box
                sx={{
                    background: '#f8f9fa',
                    py: 10,
                    display: 'grid',
                    placeItems: 'center',
                }}
            >
                <Grid container maxWidth="lg" spacing={6} justifyContent="center" alignItems="center">
                    <Grid item xs={12} md={6}>
                        <Typography
                            variant="overline"
                            color="prevailRed.main"
                            fontWeight="bold"
                            sx={{ letterSpacing: 3, fontSize: '0.9em' }}
                        >
                            THE PREVAIL APPROACH
                        </Typography>
                        <Typography variant="h3" color="grey.800" fontWeight="bold" mt={1} mb={3}>
                            Collect Everything.<br />
                            <Typography component="span" variant="h3" color="prevailRed.main" fontWeight="bold">
                                Reduce what is saved.
                            </Typography>
                        </Typography>
                        <Typography variant="body1" color="grey.700" mb={3} sx={{ fontSize: '1.1em', lineHeight: 1.8 }}>
                            PREVAIL SIEM ingests 100% of your log data — every event, every connection,
                            every authentication, every operation. Nothing is discarded at collection.
                            The difference is what happens next.
                        </Typography>
                        <Typography variant="body1" color="grey.700" mb={3} sx={{ fontSize: '1.1em', lineHeight: 1.8 }}>
                            Instead of writing every raw log line to disk, PREVAIL's processing pipeline
                            reduces each event to its <strong>behavioral metadata</strong> — the who, what,
                            when, where, and how-many that matter for detection and investigation — while
                            discarding the verbose formatting and redundant content that makes raw logs
                            so expensive to store.
                        </Typography>
                        <Typography variant="body1" color="grey.700" sx={{ fontSize: '1.1em', lineHeight: 1.8 }}>
                            The result: Up to 90% storage reduction compared to raw log retention, with
                            no loss of forensic capability, behavioral context, or compliance auditability.
                            You keep the intelligence. You stop paying for the noise.
                        </Typography>
                    </Grid>
                    <Grid item xs={12} md={5} sx={{ display: { xs: 'none', md: 'block' } }}>
                        {/* Visual metaphor: raw vs. reduced */}
                        <Box sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 8px 32px rgba(0,0,0,0.12)' }}>
                            {/* Raw logs side */}
                            <Box sx={{ background: '#1a1929', p: 3, borderBottom: '2px solid #d90000' }}>
                                <Typography variant="overline" color="grey.500" sx={{ letterSpacing: 2 }}>
                                    RAW LOG — 847 bytes stored
                                </Typography>
                                <Typography
                                    variant="body2"
                                    color="grey.500"
                                    sx={{
                                        fontFamily: 'monospace',
                                        fontSize: '0.72em',
                                        mt: 1,
                                        lineHeight: 1.7,
                                        wordBreak: 'break-all',
                                    }}
                                >
                                    2024-01-15T14:23:11.483Z WARN [AuthService] user=john.doe@acme.com
                                    action=SIGNIN result=SUCCESS ip=203.0.113.42 location="Lagos, NG"
                                    device="Mozilla/5.0 Windows NT 10.0; Win64; x64" session=8f3a...
                                    tenant=acme.onmicrosoft.com mfa=bypassed protocol=OWA
                                    prev_ip=192.168.1.45 prev_location="New York, US" delta_hours=0.3
                                    risk_score=0.0 conditional_access=passed ...
                                </Typography>
                            </Box>
                            {/* Arrow */}
                            <Box sx={{ background: '#242331', p: 1.5, textAlign: 'center' }}>
                                <Typography variant="caption" color="prevailRed.main" fontWeight="bold" sx={{ letterSpacing: 2 }}>
                                    ▼ PREVAIL METADATA REDUCTION
                                </Typography>
                            </Box>
                            {/* Metadata side */}
                            <Box sx={{ background: '#0f2d0f', p: 3 }}>
                                <Typography variant="overline" color="trawlgreen.main" sx={{ letterSpacing: 2 }}>
                                    METADATA — 61 bytes stored
                                </Typography>
                                <Typography
                                    variant="body2"
                                    color="grey.400"
                                    sx={{
                                        fontFamily: 'monospace',
                                        fontSize: '0.72em',
                                        mt: 1,
                                        lineHeight: 1.7,
                                    }}
                                >
                                    user=john.doe action=SIGNIN<br />
                                    geo_delta=NY→NG time_delta=0.3h<br />
                                    mfa_bypassed=true anomaly=HIGH
                                </Typography>
                            </Box>
                        </Box>
                        <Typography variant="caption" color="grey.500" sx={{ display: 'block', mt: 1.5, textAlign: 'center', fontStyle: 'italic' }}>
                            Illustrative example. Same detection signal. 93% less storage. Savings vary by device.
                        </Typography>
                    </Grid>
                </Grid>
            </Box>

            {/* ─── THREE-WAY COMPARISON ─── */}
            <Box
                sx={{
                    background: '#ffffff',
                    py: 10,
                    display: 'grid',
                    placeItems: 'center',
                }}
            >
                <Grid container maxWidth="lg" spacing={3} justifyContent="center">
                    <Grid item  size={{ xs: 12 }} textAlign="center" mb={4}>
                        <Typography variant="h3" color="grey.800" fontWeight="bold">
                            How PREVAIL Compares
                        </Typography>
                        <Typography variant="h6" color="grey.600" mt={2} sx={{ maxWidth: '680px', mx: 'auto' }}>
                            There are two wrong approaches to the log storage problem.
                            PREVAIL takes a third path.
                        </Typography>
                    </Grid>

                    {/* Splunk / Sentinel */}
                    <Grid item size={{ xs: 12, md: 5 }}>
                        <Box
                            sx={{
                                border: '2px solid #e0e0e0',
                                borderRadius: 2,
                                p: 4,
                                height: '100%',
                            }}
                        >
                            <Typography variant="overline" color="grey.500" sx={{ letterSpacing: 2 }}>
                                SPLUNK / SENTINEL
                            </Typography>
                            <Typography variant="h5" color="grey.700" fontWeight="bold" mt={1} mb={3}>
                                Store Everything Raw
                            </Typography>
                            <Typography variant="body2" color="grey.600" mb={3} lineHeight={1.8}>
                                Full fidelity log ingestion and storage. Every event written to disk exactly
                                as received. Maximum forensic flexibility — at a cost that scales
                                exponentially with your log volume.
                            </Typography>
                            <Divider sx={{ mb: 3 }} />
                            {[
                                { pro: true, text: 'Complete raw log access' },
                                { pro: true, text: 'Maximum forensic detail' },
                                { pro: false, text: 'Priced per GB ingested — costs explode at scale' },
                                { pro: false, text: 'Enterprise pricing, out of reach for SMBs' },
                                { pro: false, text: 'Requires dedicated security analysts to operate' },
                                { pro: false, text: 'Complex configuration and tuning required' },
                            ].map(({ pro, text }) => (
                                <Box key={text} display="flex" alignItems="flex-start" gap={1.5} mb={1.5}>
                                    {pro
                                        ? <CheckCircleIcon sx={{ color: '#47A025', fontSize: 18, flexShrink: 0, mt: 0.2 }} />
                                        : <CancelIcon sx={{ color: '#d90000', fontSize: 18, flexShrink: 0, mt: 0.2 }} />
                                    }
                                    <Typography variant="body2" color="grey.600" sx={{ fontSize: '0.92em' }}>{text}</Typography>
                                </Box>
                            ))}
                        </Box>
                    </Grid>

                    {/* Discard-based */}
                    <Grid item  size={{ xs: 12, md: 5 }}>
                        <Box
                            sx={{
                                border: '2px solid #e0e0e0',
                                borderRadius: 2,
                                p: 4,
                                height: '100%',
                            }}
                        >
                            <Typography variant="overline" color="grey.500" sx={{ letterSpacing: 2 }}>
                                DISCARD-BASED TOOLS
                            </Typography>
                            <Typography variant="h5" color="grey.700" fontWeight="bold" mt={1} mb={3}>
                                Collect, Then Discard
                            </Typography>
                            <Typography variant="body2" color="grey.600" mb={3} lineHeight={1.8}>
                                Process logs on receipt, extract alerts, then delete the raw data.
                                Storage costs stay low — but so does your ability to investigate
                                after an incident. The forensic trail disappears with the logs.
                            </Typography>
                            <Divider sx={{ mb: 3 }} />
                            {[
                                { pro: true, text: 'Lower storage costs than raw retention' },
                                { pro: true, text: 'Real-time alerting on processed events' },
                                { pro: false, text: 'No forensic history after logs are discarded' },
                                { pro: false, text: 'Cannot retroactively investigate past events' },
                                { pro: false, text: 'Compliance gaps where audit logs are required' },
                                { pro: false, text: 'Blind to slow, multi-stage attack patterns' },
                            ].map(({ pro, text }) => (
                                <Box key={text} display="flex" alignItems="flex-start" gap={1.5} mb={1.5}>
                                    {pro
                                        ? <CheckCircleIcon sx={{ color: '#47A025', fontSize: 18, flexShrink: 0, mt: 0.2 }} />
                                        : <CancelIcon sx={{ color: '#d90000', fontSize: 18, flexShrink: 0, mt: 0.2 }} />
                                    }
                                    <Typography variant="body2" color="grey.600" sx={{ fontSize: '0.92em' }}>{text}</Typography>
                                </Box>
                            ))}
                        </Box>
                    </Grid>

                    {/* PREVAIL */}
                    <Grid item  size={{ xs: 12, md: 5 }}>
                        <Box
                            sx={{
                                border: '2px solid #d90000',
                                borderRadius: 2,
                                p: 4,
                                height: '100%',
                                boxShadow: '0 8px 24px rgba(217,0,0,0.12)',
                            }}
                        >
                            <Box display="flex" alignItems="center" gap={1} mb={0}>
                                <Typography variant="overline" color="prevailRed.main" sx={{ letterSpacing: 2 }}>
                                    PREVAIL SIEM
                                </Typography>
                                <Chip
                                    label="NEW"
                                    size="small"
                                    sx={{
                                        background: '#d90000',
                                        color: 'white',
                                        fontWeight: 'bold',
                                        fontSize: '0.65em',
                                        height: 18,
                                    }}
                                />
                            </Box>
                            <Typography variant="h5" color="prevailRed.main" fontWeight="bold" mt={1} mb={3}>
                                Reduce to Metadata
                            </Typography>
                            <Typography variant="body2" color="grey.700" mb={3} lineHeight={1.8}>
                                Collect 100% of log data. Process and reduce every event to its behavioral
                                metadata. Keep the intelligence permanently — discard only the redundant
                                formatting. Full forensic capability at a fraction of the storage cost.
                            </Typography>
                            <Divider sx={{ mb: 3 }} />
                            {[
                                { pro: true, text: 'All events collected — nothing discarded at ingestion' },
                                { pro: true, text: '90%+ storage reduction vs. raw log retention' },
                                { pro: true, text: 'Full forensic and investigation capability retained' },
                                { pro: true, text: 'Long-term retention without exponential cost growth' },
                                { pro: true, text: 'Affordable for SMBs — no per-GB ingestion fees' },
                                { pro: true, text: 'Behavioral AI detection included' },
                            ].map(({ pro, text }) => (
                                <Box key={text} display="flex" alignItems="flex-start" gap={1.5} mb={1.5}>
                                    <CheckCircleIcon sx={{ color: '#47A025', fontSize: 18, flexShrink: 0, mt: 0.2 }} />
                                    <Typography variant="body2" color="grey.700" sx={{ fontSize: '0.92em', fontWeight: pro ? 'normal' : 'normal' }}>{text}</Typography>
                                </Box>
                            ))}
                        </Box>
                    </Grid>
                </Grid>
            </Box>

            {/* ─── HOW IT WORKS ─── */}
            <Box
                sx={{
                    background: '#242331',
                    py: 10,
                    display: 'grid',
                    placeItems: 'center',
                }}
            >
                <Grid container maxWidth="lg" spacing={2} justifyContent="center">
                    <Grid item xs={12} textAlign="center" mb={6}>
                        <Typography
                            variant="overline"
                            color="prevailRed.main"
                            fontWeight="bold"
                            sx={{ letterSpacing: 3, fontSize: '0.9em' }}
                        >
                            HOW IT WORKS
                        </Typography>
                        <Typography variant="h3" color="grey.100" fontWeight="bold" mt={1}>
                            Collect. Reduce. Retain. Detect.
                        </Typography>
                        <Typography variant="h6" color="grey.500" mt={2} sx={{ maxWidth: '650px', mx: 'auto' }}>
                            A four-stage pipeline that gives you everything a traditional SIEM offers —
                            without the storage bill that comes with it.
                        </Typography>
                    </Grid>

                    {[
                        {
                            icon: <CloudDownloadIcon sx={{ fontSize: 56 }} />,
                            step: '01',
                            title: 'Collect',
                            description:
                                'PREVAIL ingests log streams from servers, network devices, cloud services, and endpoints — 100% of events, in real time. Nothing is filtered or dropped at the collection stage. Every event enters the pipeline.',
                        },
                        {
                            icon: <CompressIcon sx={{ fontSize: 56 }} />,
                            step: '02',
                            title: 'Reduce',
                            description:
                                'Each event is processed and reduced to its behavioral metadata: identity, action, resource, location, timing, and anomaly context. The raw log is used — then replaced by its compact metadata equivalent. Storage drops by over 90%.',
                        },
                        {
                            icon: <StorageIcon sx={{ fontSize: 56 }} />,
                            step: '03',
                            title: 'Retain',
                            description:
                                'Metadata is stored long-term — years, not days. Because storage requirements are a fraction of raw log volume, you can afford retention periods that support compliance, incident response, and trend analysis without cost escalation.',
                        },
                        {
                            icon: <InsightsIcon sx={{ fontSize: 56 }} />,
                            step: '04',
                            title: 'Detect',
                            description:
                                'PREVAIL\'s behavioral AI runs continuously across the retained metadata, building baselines and detecting anomalies across your environment. Threats are surfaced in real time — not after a human reviews a dashboard.',
                        },
                    ].map(({ icon, step, title, description }) => (
                        <Grid item xs={12} md={3} key={step} p={2}>
                            <Box
                                sx={{
                                    border: '2px solid rgba(255,255,255,0.1)',
                                    borderRadius: 2,
                                    p: 4,
                                    height: '100%',
                                    position: 'relative',
                                    transition: 'border-color 0.3s',
                                    '&:hover': { borderColor: '#d90000' },
                                }}
                            >
                                <Typography
                                    sx={{
                                        position: 'absolute',
                                        top: 12,
                                        right: 18,
                                        fontSize: '3.5em',
                                        fontWeight: 900,
                                        color: 'rgba(217,0,0,0.07)',
                                        lineHeight: 1,
                                        userSelect: 'none',
                                    }}
                                >
                                    {step}
                                </Typography>
                                <Typography color="prevailRed.main" mb={2}>{icon}</Typography>
                                <Typography variant="h5" color="grey.100" fontWeight="bold" mb={2}>{title}</Typography>
                                <Typography variant="body2" color="grey.400" lineHeight={1.8} sx={{ fontSize: '0.97em' }}>
                                    {description}
                                </Typography>
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            </Box>

            {/* ─── WHAT YOU KEEP ─── */}
            <Box
                sx={{
                    background: '#f8f9fa',
                    py: 10,
                    display: 'grid',
                    placeItems: 'center',
                }}
            >
                <Grid container maxWidth="lg" spacing={4} justifyContent="center">
                    <Grid item xs={12} textAlign="center" mb={4}>
                        <Typography variant="h3" color="grey.800" fontWeight="bold">
                            What You Don't Give Up
                        </Typography>
                        <Typography variant="h6" color="grey.600" mt={2} sx={{ maxWidth: '680px', mx: 'auto' }}>
                            Metadata reduction is not data loss. Every critical capability of a full
                            SIEM is preserved — you just stop paying to store what you never needed.
                        </Typography>
                    </Grid>

                    {[
                        {
                            icon: <SearchIcon sx={{ fontSize: 40 }} />,
                            title: 'Incident Investigation',
                            description:
                                'When a breach occurs, you need to answer: who accessed what, from where, and when. PREVAIL\'s retained metadata answers every one of those questions — giving your team or MSP everything needed to scope and contain an incident.',
                        },
                        {
                            icon: <GavelIcon sx={{ fontSize: 40 }} />,
                            title: 'Compliance & Audit Trails',
                            description:
                                'Regulatory frameworks like HIPAA, PCI-DSS, and SOC 2 require that you demonstrate who had access to systems and when. PREVAIL\'s metadata retention supports long-term compliance audit requirements without per-event storage costs.',
                        },
                        {
                            icon: <InsightsIcon sx={{ fontSize: 40 }} />,
                            title: 'Behavioral Baselines & Trend Analysis',
                            description:
                                'Because PREVAIL retains behavioral metadata across time, it can build rich user and system profiles — detecting slow-moving threats that only become visible over weeks or months of activity.',
                        },
                        {
                            icon: <SpeedIcon sx={{ fontSize: 40 }} />,
                            title: 'Real-Time Threat Detection',
                            description:
                                'Metadata reduction happens at write time, not query time. Detection runs against the same pipeline in real time — giving you sub-minute alerting on active threats without any performance trade-off.',
                        },
                        {
                            icon: <SecurityIcon sx={{ fontSize: 40 }} />,
                            title: 'Cross-Source Correlation',
                            description:
                                'PREVAIL correlates metadata across log sources — network, endpoint, authentication, cloud — to detect multi-stage attacks that no single source would reveal. The full picture, without the full storage bill.',
                        },
                        {
                            icon: <StorageIcon sx={{ fontSize: 40 }} />,
                            title: 'Long-Term Retention',
                            description:
                                'With storage costs reduced by over 90%, PREVAIL can offer retention periods that would be prohibitively expensive in a raw-log SIEM. Years of event history, available for investigation or trend analysis, at a predictable monthly cost.',
                        },
                    ].map(({ icon, title, description }) => (
                        <Grid item xs={12} md={4} key={title}>
                            <Box
                                sx={{
                                    border: '2px solid',
                                    borderColor: 'grey.200',
                                    borderRadius: 2,
                                    p: 4,
                                    height: '100%',
                                    transition: 'border-color 0.3s',
                                    '&:hover': { borderColor: '#d90000' },
                                }}
                            >
                                <Typography color="prevailRed.main" mb={2}>{icon}</Typography>
                                <Typography variant="h5" color="grey.800" fontWeight="bold" mb={2}>{title}</Typography>
                                <Typography variant="body2" color="grey.600" lineHeight={1.8}>{description}</Typography>
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            </Box>

            {/* ─── PAIRED WITH MS365 ─── */}
            <Box
                sx={{
                    background: '#242331',
                    py: 10,
                    display: 'grid',
                    placeItems: 'center',
                }}
            >
                <Grid container maxWidth="lg" spacing={6} justifyContent="center" alignItems="center">
                    <Grid item xs={12} md={6}>
                        <Typography
                            variant="overline"
                            color="prevailRed.main"
                            fontWeight="bold"
                            sx={{ letterSpacing: 3, fontSize: '0.9em' }}
                        >
                            COMPLETE COVERAGE
                        </Typography>
                        <Typography variant="h3" color="grey.100" fontWeight="bold" mt={1} mb={3}>
                            Stronger Together with PREVAIL Microsoft 365
                        </Typography>
                        <Typography variant="body1" color="grey.300" mb={3} sx={{ fontSize: '1.1em', lineHeight: 1.8 }}>
                            PREVAIL SIEM is designed to work alongside PREVAIL's Microsoft 365 behavioral
                            detection — extending coverage from cloud identity and productivity applications
                            into your network, server, and endpoint log sources.
                        </Typography>
                        <Typography variant="body1" color="grey.300" mb={4} sx={{ fontSize: '1.1em', lineHeight: 1.8 }}>
                            Together, they give you a unified behavioral intelligence layer across your
                            entire environment — cloud and on-premise — at a cost that works for
                            businesses of any size.
                        </Typography>
                        {[
                            'Unified behavioral profiles across cloud and network sources',
                            'Correlated detection across Microsoft 365 and infrastructure logs',
                            'Single platform for alerts, investigation, and reporting',
                            'One subscription covering your full attack surface',
                        ].map((point) => (
                            <Box key={point} display="flex" alignItems="flex-start" gap={1.5} mb={1.5}>
                                <CheckCircleIcon sx={{ color: '#47A025', mt: 0.2, fontSize: 20, flexShrink: 0 }} />
                                <Typography variant="body2" color="grey.300" sx={{ fontSize: '1em' }}>{point}</Typography>
                            </Box>
                        ))}
                    </Grid>

                </Grid>
            </Box>

            {/* ─── EARLY ACCESS CTA ─── */}
            <Box
                sx={{
                    background: 'linear-gradient(135deg, #2D0000 0%, #6b0000 60%, #242331 100%)',
                    py: 12,
                    display: 'grid',
                    placeItems: 'center',
                    textAlign: 'center',
                }}
            >
                <Grid container maxWidth="md" spacing={2} justifyContent="center">
                    <Grid item xs={12}>
                        <Chip
                            label="COMING SOON"
                            sx={{
                                background: 'rgba(255,255,255,0.1)',
                                border: '1px solid rgba(255,255,255,0.3)',
                                color: 'grey.300',
                                fontWeight: 'bold',
                                letterSpacing: 2,
                                mb: 3,
                            }}
                        />
                        <Typography variant="h3" color="grey.100" fontWeight="bold" mb={2}>
                            Be First to Know When It Launches
                        </Typography>
                        <Typography variant="h6" color="grey.400" mb={5} sx={{ maxWidth: '580px', mx: 'auto' }}>
                            PREVAIL SIEM is in development. Contact us to join the early access list,
                            request a demo, or discuss how it fits your environment.
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
                            <Link to="/contact-us">
                                <Button
                                    variant="contained"
                                    color="prevailRed"
                                    size="large"
                                    sx={{ pl: 6, pr: 6, py: 2 }}
                                >
                                    <Typography sx={{ fontSize: '1.2em' }} color="grey.100">
                                        Request Early Access
                                    </Typography>
                                </Button>
                            </Link>
                            <Link to="/products/ms365">
                                <Button
                                    variant="outlined"
                                    size="large"
                                    sx={{ pl: 5, pr: 5, py: 2, borderColor: 'grey.500', color: 'grey.300' }}
                                >
                                    <Typography sx={{ fontSize: '1.2em' }}>See Microsoft 365 Protection</Typography>
                                </Button>
                            </Link>
                        </Box>
                    </Grid>
                </Grid>
            </Box>
        </>
    );
}
